/**
 * Aircraft catalog for the studio.
 *
 * Mirrors `demo/masters-planner/data/loadAircraftCatalog.ts` — same table, same
 * model-URL resolution order, so a model renders here exactly as it does on the
 * ramp — with one deliberate difference: types missing physical dimensions are
 * KEPT rather than skipped.
 *
 * The planner drops them because its placement geometry needs a real footprint.
 * A promo stage has no such constraint: the operator is composing a shot by eye,
 * and silently hiding a third of the fleet from the picker would be worse than
 * substituting nominal dimensions (which only feed the procedural fallback used
 * when a type has no GLB at all).
 */
import { supabase } from '@/lib/supabase'
import type { AircraftPhysicalDimensions, AircraftType3D, ModelSettings } from '@/services/3d'

/** Stand-ins for types with no dimensions recorded — a mid-size business jet. */
const FALLBACK_LENGTH_FT = 50
const FALLBACK_WINGSPAN_FT = 50
const FALLBACK_HEIGHT_FT = 15

export interface StudioCatalogEntry {
  typeId: string
  typeCode: string
  label: string
  manufacturer: string | null
  model: string | null
  category: string | null
  aircraftType: AircraftType3D
  dimensions: AircraftPhysicalDimensions
  /** Perspective render from R2, for the picker thumbnail. */
  thumbnailUrl: string | null
  /** False when we substituted nominal dimensions. */
  hasRealDimensions: boolean
  /** False when the type has no GLB and will render procedurally. */
  hasModel: boolean
}

export type StudioCatalog = Map<string, StudioCatalogEntry>

interface Row {
  id: string
  type_code: string | null
  manufacturer: string | null
  model: string | null
  model_url: string | null
  /**
   * `model_settings` is `Json` in the generated DB types, so it arrives
   * untyped. Treat it as `ModelSettings` at this one boundary — the shape is
   * written by `scripts/deploy-models` and consumed by `AircraftLoaderService`,
   * which tolerates missing fields.
   */
  model_settings: ModelSettings | null
  length: number | null
  wingspan: number | null
  height: number | null
  tail_height: number | null
  category: string | null
}

export async function loadStudioCatalog(): Promise<StudioCatalog> {
  const { data, error } = await supabase
    .from('aircraft_types')
    .select(
      'id, type_code, manufacturer, model, model_url, model_settings, length, wingspan, height, tail_height, category'
    )
    .eq('is_active', true)
    .order('manufacturer')
    .order('model')

  if (error) throw error

  const catalog: StudioCatalog = new Map()

  for (const row of (data ?? []) as Row[]) {
    if (!row.type_code) continue

    const modelSettings = row.model_settings ?? {}
    const modelUrl = row.model_url || modelSettings.models?.lod1?.url || modelSettings.filePath

    const hasRealDimensions = !!(row.length && row.wingspan && row.height)

    const label = [row.manufacturer, row.model].filter(Boolean).join(' ') || row.type_code

    catalog.set(row.type_code, {
      typeId: row.id,
      typeCode: row.type_code,
      label,
      manufacturer: row.manufacturer,
      model: row.model,
      category: row.category,
      aircraftType: {
        id: row.id,
        typeCode: row.type_code,
        modelSettings: { ...modelSettings, filePath: modelUrl || undefined },
      },
      dimensions: {
        length: row.length ?? FALLBACK_LENGTH_FT,
        wingspan: row.wingspan ?? FALLBACK_WINGSPAN_FT,
        height: row.height ?? FALLBACK_HEIGHT_FT,
        tailHeight: row.tail_height ?? null,
        category: (row.category ?? null) as AircraftPhysicalDimensions['category'],
        gearType: null,
      },
      thumbnailUrl: modelSettings.images?.perspective ?? modelSettings.images?.top ?? null,
      hasRealDimensions,
      hasModel: !!modelUrl,
    })
  }

  return catalog
}
